import { SectionCard, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@atlas/ui-components'
import { Badge } from '@atlas/ui-primitives'
import { useQueries } from '@tanstack/react-query'
import { useAxes } from '@/hooks/useClassification'
import { classificationApi } from '@/services/classification.service'

/**
 * Read-only systematic overview of the whole taxonomy (design D5). Renders
 * dynamically from the axis registry — no hard-coded dimension names. Shows every
 * axis with kind, value count, hierarchy depth, and sample values.
 */
interface OverviewRow {
	axis: ReturnType<typeof useAxes>['data'] extends (infer T)[] | undefined ? T : never
	count: number
	depth: number
	samples: string[]
}

function OverviewTable({ rows }: { rows: OverviewRow[] }) {
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Axis</TableHead>
					<TableHead>Kind</TableHead>
					<TableHead>Type</TableHead>
					<TableHead>Values</TableHead>
					<TableHead>Depth</TableHead>
					<TableHead>Sample values</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{rows.map(({ axis, count, depth, samples }) => (
					<TableRow key={axis.axisId}>
						<TableCell className="font-medium">
							{axis.name}
							<span className="ml-2 text-xs text-ds-text-subtle">{axis.code}</span>
						</TableCell>
						<TableCell>
							<Badge variant={axis.kind === 'dimension' ? 'primary' : 'secondary'} size="sm">
								{axis.kind}
							</Badge>
						</TableCell>
						<TableCell className="text-ds-text-muted">{axis.valueType}</TableCell>
						<TableCell>{count}</TableCell>
						<TableCell>{axis.isHierarchical ? depth : '—'}</TableCell>
						<TableCell className="text-ds-text-muted text-sm">
							{samples.length ? (
								samples.join(', ') + (count > samples.length ? '…' : '')
							) : (
								<span className="text-ds-text-subtle">none yet</span>
							)}
						</TableCell>
					</TableRow>
				))}
			</TableBody>
		</Table>
	)
}

export function SystematicViewPage() {
	const { data: axes = [], isLoading } = useAxes()
	const valueQueries = useQueries({
		queries: axes.map((a) => ({
			queryKey: ['values', a.axisId],
			queryFn: () => classificationApi.listValues(a.axisId),
		})),
	})
	const rows: OverviewRow[] = axes.map((axis, i) => {
		const values = valueQueries[i]?.data ?? []
		const hasParents = values.some((v) => v.parentId)
		const hasGrandparent = values.some((v) => v.parentId && values.find((p) => p.valueId === v.parentId)?.parentId)
		const depth = axis.isHierarchical ? (hasGrandparent ? 3 : hasParents ? 2 : 1) : 1
		return { axis, count: values.length, depth, samples: values.slice(0, 4).map((v) => v.label) }
	})
	const dims = axes.filter((a) => a.kind === 'dimension').length
	const attrs = axes.filter((a) => a.kind === 'attribute').length
	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-semibold">Taxonomy — Systematic View</h1>
				<p className="text-ds-text-muted">
					{isLoading ? 'Loading…' : `${axes.length} axes · ${dims} dimensions · ${attrs} attributes`}
				</p>
			</div>
			<SectionCard
				title="Axis registry"
				description="The classification model at a glance — rendered live from the registry."
			>
				<OverviewTable rows={rows} />
			</SectionCard>
		</div>
	)
}
